import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Zap, Plus, Settings, Trash2, Check, Clock, Bell,
  AlertCircle, PlayCircle, PauseCircle, Edit, Copy,
  Loader2, ChevronRight, Target, Mail, MessageSquare,
  Bot, ArrowRight, ArrowLeft, Calendar, Users, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const AutomationsPage = () => {
  const navigate = useNavigate();
  // When the page is opened from a specific AI Employee (via the Automations
  // tab in ChatbotDetailPage), we get `?chatbot_id=...`. We mirror that
  // employee's header so the look-and-feel stays consistent across tabs.
  const contextChatbotId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('chatbot_id');
  }, []);
  const [loading, setLoading] = useState(true);
  const [chatbots, setChatbots] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedChatbot, setSelectedChatbot] = useState('');
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    trigger_type: 'stage_duration',
    trigger_config: {
      stage: 'contacted',
      duration_days: 3
    },
    action_type: 'notification',
    action_config: {
      title: 'Follow-up Reminder',
      message: 'Lead has been waiting for {duration} days in {stage} stage.',
      priority: 'medium'
    },
    active: true
  });

  const stages = [
    { value: 'raw', label: 'New/Raw' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'engaged', label: 'Engaged' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'mql', label: 'MQL' },
    { value: 'sql', label: 'SQL' },
    { value: 'proposal_sent', label: 'Proposal Sent' },
    { value: 'negotiation', label: 'Negotiation' }
  ];

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [chatbotsRes, automationsRes] = await Promise.all([
        api.get('/chatbots'),
        api.get('/automations')
      ]);
      
      const chatbotsData = Array.isArray(chatbotsRes.data) ? chatbotsRes.data : chatbotsRes.data.chatbots || [];
      setChatbots(chatbotsData);
      setAutomations(automationsRes.data || []);
      
      if (chatbotsData.length > 0 && !selectedChatbot) {
        // Honor ?chatbot_id=... query param if provided (deep-link from agent detail page)
        const params = new URLSearchParams(window.location.search);
        const requested = params.get('chatbot_id');
        const initial = requested && chatbotsData.find((b) => b.chatbot_id === requested)
          ? requested
          : chatbotsData[0].chatbot_id;
        setSelectedChatbot(initial);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setAutomations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAutomation = async () => {
    if (!selectedChatbot) {
      toast.error('Please select an AI Employee');
      return;
    }
    
    if (!formData.name) {
      toast.error('Please enter a name for the automation');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        chatbot_id: selectedChatbot
      };
      
      if (editingAutomation) {
        await api.put(`/automations/${editingAutomation.automation_id}`, payload);
        toast.success('Automation updated successfully!');
      } else {
        await api.post('/automations', payload);
        toast.success('Automation created successfully!');
      }
      
      setShowAddModal(false);
      setEditingAutomation(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save automation');
    } finally {
      setSaving(false);
    }
  };

  const [testing, setTesting] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  const isEmailAction = formData.action_type === 'email' || formData.action_type === 'email_sequence';

  /**
   * Send a dry-run of the automation to a synthetic recipient so admins can
   * validate the connected email channel + template rendering *before*
   * flipping the automation live. Uses the same /execute code path as the
   * production trigger, so any misconfiguration (missing channel, bad
   * template placeholders, provider auth failure) surfaces immediately.
   */
  const handleSendTestEmail = async () => {
    if (!editingAutomation?.automation_id) {
      toast.error('Save the automation first — the test needs a stored automation to execute against.');
      return;
    }
    const recipient = (testRecipient || '').trim();
    if (!recipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast.error('Enter a valid email address to receive the test.');
      return;
    }
    setTesting(true);
    try {
      const res = await api.post(`/automations/${editingAutomation.automation_id}/execute`, {
        lead_email: recipient,
        lead_name: 'Test Lead',
      });
      const provider = res.data?.provider ? ` (via ${res.data.provider})` : '';
      toast.success(`Test email sent to ${recipient}${provider}. Check the inbox in a few seconds.`, { duration: 10000 });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === 'string' && detail.length > 0
          ? detail
          : 'Automation test failed. Check the backend logs.',
        { duration: 14000 }
      );
    } finally {
      setTesting(false);
    }
  };

  const handleEditAutomation = (automation) => {
    setEditingAutomation(automation);
    setFormData({
      name: automation.name,
      description: automation.description || '',
      trigger_type: automation.trigger_type,
      trigger_config: automation.trigger_config,
      action_type: automation.action_type,
      action_config: automation.action_config,
      active: automation.active
    });    setSelectedChatbot(automation.chatbot_id);
    setShowAddModal(true);
  };

  const handleDeleteAutomation = async (automationId) => {
    if (!confirm('Are you sure you want to delete this automation?')) return;
    
    try {
      await api.delete(`/automations/${automationId}`);
      toast.success('Automation deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete automation');
    }
  };

  const handleToggleActive = async (automationId, active) => {
    try {
      await api.patch(`/automations/${automationId}`, { active });
      toast.success(active ? 'Automation activated' : 'Automation paused');
      loadData();
    } catch (error) {
      toast.error('Failed to update automation');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      trigger_type: 'stage_duration',
      trigger_config: {
        stage: 'contacted',
        duration_days: 3
      },
      action_type: 'notification',
      action_config: {
        title: 'Follow-up Reminder',
        message: 'Lead has been waiting for {duration} days in {stage} stage.',
        priority: 'medium'
      },
      active: true
    });
  };

  const getTriggerDescription = (automation) => {
    const { trigger_type, trigger_config } = automation;
    switch (trigger_type) {
      case 'stage_duration':
        return `Lead in "${trigger_config.stage}" for ${trigger_config.duration_days} days`;
      case 'no_activity':
        return `No activity for ${trigger_config.days} days`;
      case 'score_change':
        return `Score drops below ${trigger_config.threshold}`;
      default:
        return 'Custom trigger';
    }
  };

  const getActionDescription = (automation) => {
    const { action_type, action_config } = automation;
    switch (action_type) {
      case 'notification':
        return `Send notification: "${action_config.title}"`;
      case 'email':
        return `Send email reminder`;
      case 'stage_change':
        return `Move to "${action_config.target_stage}"`;
      default:
        return 'Custom action';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automations-page">
      {/* Header — when opened from a specific AI Employee (`?chatbot_id=...`)
          we mirror that employee's header (back arrow + bot icon + name) so
          this tab feels like a continuation, not a context switch. The
          Dialog block below renders the same Create-Automation modal in
          both layouts. */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {contextChatbotId ? (
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(`/dashboard/chatbots/${contextChatbotId}`)}
              className="text-slate-500 hover:text-slate-900 flex-shrink-0"
              data-testid="automations-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0">
              <Bot className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900 truncate">
                {chatbots.find(b => b.chatbot_id === contextChatbotId)?.name || 'AI Employee'}
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                <Zap className="w-3.5 h-3.5 text-blue-600" />
                Follow-up automations
              </p>
            </div>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Zap className="w-7 h-7 text-blue-600" />
              Follow-Up Automations
            </h1>
            <p className="text-slate-600 mt-1">
              Create automated reminders and actions for lead follow-ups
            </p>
          </div>
        )}
        <Dialog open={showAddModal} onOpenChange={(open) => {
          setShowAddModal(open);
          if (!open) {
            setEditingAutomation(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-automation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Automation
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-blue-600" />
                {editingAutomation ? 'Edit Automation' : 'Create Follow-Up Automation'}
              </DialogTitle>
              <DialogDescription>
                Set up automatic reminders when leads need attention
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-slate-700">Automation Name</Label>
                    <Input 
                      placeholder="e.g., 3-Day Follow-up Reminder"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="automation-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-700">AI Employee</Label>
                    <Select value={selectedChatbot} onValueChange={setSelectedChatbot}>
                      <SelectTrigger data-testid="chatbot-select">
                        <SelectValue placeholder="Select AI Employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {chatbots.map((bot) => (
                          <SelectItem key={bot.chatbot_id} value={bot.chatbot_id}>
                            <span className="flex items-center gap-2">
                              <Bot className="w-4 h-4" />
                              {bot.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Trigger Configuration */}
              <Card className="border-slate-200 bg-slate-50/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                    <Clock className="w-5 h-5 text-blue-600" />
                    When to Trigger
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Trigger Type</Label>
                    <Select 
                      value={formData.trigger_type} 
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        trigger_type: value,
                        trigger_config: value === 'stage_duration' 
                          ? { stage: 'contacted', duration_days: 3 }
                          : value === 'no_activity'
                          ? { days: 5 }
                          : { threshold: 40 }
                      })}
                    >
                      <SelectTrigger data-testid="trigger-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="stage_duration">Lead stays in stage for X days</SelectItem>
                        <SelectItem value="no_activity">No activity for X days</SelectItem>
                        <SelectItem value="score_change">Lead score drops below threshold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.trigger_type === 'stage_duration' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-700">Stage</Label>
                        <Select 
                          value={formData.trigger_config.stage}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            trigger_config: { ...formData.trigger_config, stage: value }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {stages.map((stage) => (
                              <SelectItem key={stage.value} value={stage.value}>
                                {stage.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-700">Days</Label>
                        <Input 
                          type="number"
                          min="1"
                          max="30"
                          value={formData.trigger_config.duration_days}
                          onChange={(e) => setFormData({
                            ...formData,
                            trigger_config: { ...formData.trigger_config, duration_days: parseInt(e.target.value) || 1 }
                          })}
                        />
                      </div>
                    </div>
                  )}
                  
                  {formData.trigger_type === 'no_activity' && (
                    <div>
                      <Label className="text-slate-700">Days without activity</Label>
                      <Input 
                        type="number"
                        min="1"
                        max="30"
                        value={formData.trigger_config.days || 5}
                        onChange={(e) => setFormData({
                          ...formData,
                          trigger_config: { days: parseInt(e.target.value) || 5 }
                        })}
                      />
                    </div>
                  )}
                  
                  {formData.trigger_type === 'score_change' && (
                    <div>
                      <Label className="text-slate-700">Score threshold</Label>
                      <Input 
                        type="number"
                        min="0"
                        max="100"
                        value={formData.trigger_config.threshold || 40}
                        onChange={(e) => setFormData({
                          ...formData,
                          trigger_config: { threshold: parseInt(e.target.value) || 40 }
                        })}
                      />
                      <p className="text-xs text-slate-500 mt-1">Triggers when lead score drops below this value</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Action Configuration */}
              <Card className="border-blue-200 bg-blue-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-slate-900">
                    <Bell className="w-5 h-5 text-blue-600" />
                    What to Do
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-700">Action Type</Label>
                    <Select 
                      value={formData.action_type} 
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        action_type: value,
                        action_config: value === 'notification'
                          ? { title: 'Follow-up Reminder', message: 'Lead needs attention', priority: 'medium' }
                          : value === 'stage_change'
                          ? { target_stage: 'lost' }
                          : value === 'email_sequence'
                          ? { 
                              subject: 'Following up on your inquiry', 
                              body: 'Hi {lead_name},\n\nI wanted to follow up on your recent inquiry...',
                              delay_hours: 24,
                              sequence_steps: []
                            }
                          : { subject: '', body: '' }
                      })}
                    >
                      <SelectTrigger data-testid="action-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="notification">
                          <span className="flex items-center gap-2">
                            <Bell className="w-4 h-4" />
                            In-app Notification (Free)
                          </span>
                        </SelectItem>
                        <SelectItem value="email_sequence">
                          <span className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email Follow-up Sequence
                          </span>
                        </SelectItem>
                        <SelectItem value="stage_change">
                          <span className="flex items-center gap-2">
                            <ArrowRight className="w-4 h-4" />
                            Move to Stage
                          </span>
                        </SelectItem>
                        <SelectItem value="service_desk_reply">
                          <span className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Service Desk Reply (Zendesk / Freshdesk)
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.action_type === 'service_desk_reply' && (
                    <div className="space-y-4">
                      <div className="p-4 bg-indigo-50/70 rounded-lg border border-indigo-200">
                        <p className="text-sm text-indigo-800 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          <strong>Service Desk Public Reply</strong>
                        </p>
                        <p className="text-xs text-indigo-700 mt-1">
                          Posts a public reply on an existing Zendesk/Freshdesk ticket via the chatbot&apos;s connected Support Portal.
                          Payload must supply <code>external_ticket_id</code> at execution time (or fix one in the template below).
                        </p>
                      </div>
                      <div>
                        <Label>Reply Body Template</Label>
                        <Textarea
                          value={formData.action_config.body || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, body: e.target.value }
                          })}
                          rows={5}
                          placeholder="Hi {lead_name}, thanks for opening ticket #{ticket_id}. A human agent will follow up shortly."
                          data-testid="service-desk-body-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Placeholders: <code>{'{lead_name}'}</code>, <code>{'{lead_email}'}</code>, <code>{'{ticket_id}'}</code>
                        </p>
                      </div>
                      <div>
                        <Label>Ticket ID (optional)</Label>
                        <Input
                          value={formData.action_config.external_ticket_id || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, external_ticket_id: e.target.value }
                          })}
                          placeholder="Leave blank to require it at execution time"
                          data-testid="service-desk-ticket-id-input"
                        />
                      </div>
                    </div>
                  )}
                  {formData.action_type === 'email_sequence' && (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-100/50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-800 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <strong>Email Follow-up Sequence</strong>
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Sent through this chatbot&apos;s connected email channel. Connect Gmail (OAuth) or IMAP/SMTP under <strong>Channels &rarr; Email</strong> first, otherwise the automation will fail with &quot;email channel not connected&quot;.
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-700">Email Subject</Label>
                        <Input 
                          placeholder="Following up on your inquiry"
                          value={formData.action_config.subject || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, subject: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Email Body</Label>
                        <Textarea 
                          placeholder="Hi {lead_name},&#10;&#10;I wanted to follow up on your recent inquiry..."
                          value={formData.action_config.body || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, body: e.target.value }
                          })}
                          rows={5}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Available variables: {'{lead_name}'}, {'{company}'}, {'{email}'}, {'{agent_name}'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-700">Delay Before Sending (hours)</Label>
                        <Input 
                          type="number"
                          min="0"
                          max="168"
                          value={formData.action_config.delay_hours || 24}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, delay_hours: parseInt(e.target.value) || 24 }
                          })}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Email will be sent after this delay once trigger conditions are met
                        </p>
                      </div>
                    </div>
                  )}

                  {formData.action_type === 'notification' && (
                    <div className="space-y-4">
                      <div>
                        <Label className="text-slate-700">Notification Title</Label>
                        <Input 
                          placeholder="Follow-up Reminder"
                          value={formData.action_config.title}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, title: e.target.value }
                          })}
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Message</Label>
                        <Textarea 
                          placeholder="Lead {lead_name} has been waiting for {duration} days..."
                          value={formData.action_config.message}
                          onChange={(e) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, message: e.target.value }
                          })}
                          rows={3}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Available variables: {'{lead_name}'}, {'{duration}'}, {'{stage}'}, {'{score}'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-slate-700">Priority</Label>
                        <Select 
                          value={formData.action_config.priority}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            action_config: { ...formData.action_config, priority: value }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {formData.action_type === 'stage_change' && (
                    <div>
                      <Label className="text-slate-700">Move to Stage</Label>
                      <Select 
                        value={formData.action_config.target_stage}
                        onValueChange={(value) => setFormData({
                          ...formData,
                          action_config: { target_stage: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((stage) => (
                            <SelectItem key={stage.value} value={stage.value}>
                              {stage.label}
                            </SelectItem>
                          ))}
                          <SelectItem value="lost">Lost</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        Automatically move leads to this stage when triggered
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-slate-900 font-medium">Activate Automation</Label>
                  <p className="text-sm text-slate-500">Start running this automation immediately</p>
                </div>
                <Switch 
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
              </div>

              {/* Send test email — only shown for existing email automations
                  so admins can validate the channel + template rendering with
                  a synthetic recipient before flipping the automation live. */}
              {editingAutomation && isEmailAction && (
                <div className="p-3 border border-slate-200 rounded-lg bg-slate-50 space-y-2" data-testid="automation-test-email-panel">
                  <Label className="text-sm font-medium text-slate-800">Send a test email</Label>
                  <p className="text-xs text-slate-600">
                    Runs this automation once against a fake lead named &quot;Test Lead&quot; so you can preview the template using your connected email channel. Requires the automation to be saved first.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      type="email"
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      placeholder="your-email@example.com"
                      className="flex-1"
                      data-testid="automation-test-email-input"
                    />
                    <Button
                      variant="outline"
                      onClick={handleSendTestEmail}
                      disabled={testing}
                      data-testid="automation-send-test-email-btn"
                    >
                      {testing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                      ) : (
                        <><Mail className="w-4 h-4 mr-2" />Send test</>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveAutomation}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-automation-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editingAutomation ? 'Update Automation' : 'Create Automation'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* How Automations Work */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardContent className="py-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">1. Set Trigger</p>
                <p className="text-sm text-slate-600">Define when to act</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 rotate-90 md:rotate-0" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">2. Lead Matches</p>
                <p className="text-sm text-slate-600">System checks leads</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-300 rotate-90 md:rotate-0" />
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <Bell className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">3. Action Fires</p>
                <p className="text-sm text-slate-600">You get notified</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Automations List */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-slate-900">
            <Settings className="w-5 h-5 text-slate-600" />
            Your Automations
          </CardTitle>
          <CardDescription>
            Manage your follow-up automation rules
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            // When opened from a specific AI Employee, show only that
            // employee's automations so the listing matches the context
            // header above.
            const visibleAutomations = contextChatbotId
              ? automations.filter(a => a.chatbot_id === contextChatbotId)
              : automations;
            return visibleAutomations.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
              <Zap className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Automations Yet</h3>
              <p className="text-slate-600 mb-4 max-w-sm mx-auto">
                Create your first automation to never miss a follow-up opportunity.
              </p>
              <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Automation
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleAutomations.map((automation) => (
                <div 
                  key={automation.automation_id}
                  className={`p-4 rounded-lg border transition-all ${
                    automation.active 
                      ? 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-sm' 
                      : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid={`automation-${automation.automation_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-slate-900">{automation.name}</h4>
                        {automation.active ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                            <PlayCircle className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-slate-200">
                            <PauseCircle className="w-3 h-3 mr-1" />
                            Paused
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-blue-500" />
                          {getTriggerDescription(automation)}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <span className="flex items-center gap-1">
                          <Bell className="w-4 h-4 text-indigo-500" />
                          {getActionDescription(automation)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        For: {chatbots.find(b => b.chatbot_id === automation.chatbot_id)?.name || 'All AI Employees'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={automation.active}
                        onCheckedChange={(checked) => handleToggleActive(automation.automation_id, checked)}
                      />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEditAutomation(automation)}
                        className="text-slate-400 hover:text-blue-600"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteAutomation(automation.automation_id)}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
          })()}
        </CardContent>
      </Card>

      {/* Preset Templates */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
            Popular Automation Templates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                name: '3-Day Follow-up',
                description: 'Remind when lead is contacted but not engaged for 3 days',
                trigger: 'Stage: Contacted, 3 days',
                action: 'Notification'
              },
              {
                name: 'Cold Lead Alert',
                description: 'Alert when lead score drops significantly',
                trigger: 'Score below 30',
                action: 'High priority notification'
              },
              {
                name: 'Stale Proposal',
                description: 'Reminder for proposals sent but not closed',
                trigger: 'Proposal Sent, 7 days',
                action: 'Notification'
              }
            ].map((template, idx) => (
              <div 
                key={`tmpl-${template.name}`}
                className="p-4 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                onClick={() => {
                  // Pre-fill form based on template
                  if (template.name === '3-Day Follow-up') {
                    setFormData({
                      name: template.name,
                      description: template.description,
                      trigger_type: 'stage_duration',
                      trigger_config: { stage: 'contacted', duration_days: 3 },
                      action_type: 'notification',
                      action_config: { 
                        title: 'Follow-up Needed', 
                        message: 'Lead {lead_name} has been in Contacted stage for 3 days. Time to follow up!',
                        priority: 'medium'
                      },
                      active: true
                    });
                  } else if (template.name === 'Cold Lead Alert') {
                    setFormData({
                      name: template.name,
                      description: template.description,
                      trigger_type: 'score_change',
                      trigger_config: { threshold: 30 },
                      action_type: 'notification',
                      action_config: { 
                        title: 'Cold Lead Alert', 
                        message: 'Lead {lead_name} score has dropped to {score}. Consider re-engagement.',
                        priority: 'high'
                      },
                      active: true
                    });
                  } else {
                    setFormData({
                      name: template.name,
                      description: template.description,
                      trigger_type: 'stage_duration',
                      trigger_config: { stage: 'proposal_sent', duration_days: 7 },
                      action_type: 'notification',
                      action_config: { 
                        title: 'Stale Proposal', 
                        message: 'Proposal for {lead_name} has been pending for 7 days. Follow up to close!',
                        priority: 'high'
                      },
                      active: true
                    });
                  }
                  setShowAddModal(true);
                }}
              >
                <h4 className="font-medium text-slate-900 mb-1">{template.name}</h4>
                <p className="text-sm text-slate-600 mb-3">{template.description}</p>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="px-2 py-1 bg-blue-50 rounded text-blue-700">{template.trigger}</span>
                  <ArrowRight className="w-3 h-3" />
                  <span className="px-2 py-1 bg-indigo-50 rounded text-indigo-700">{template.action}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AutomationsPage;
